import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Form } from "@comps/ui/form";
import { Dashboard } from "@dto/Dashboard";
import { BASE_URL } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_DashboardMenuItem, TW_DashboardMenuItemIcon, useHover } from "@utils/twClasses";
import { del } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

interface DashboardMenuDeleteItemProps {
  dashboard: Dashboard;
  onDashboardDelete: (dashboard: Dashboard) => void;
  closeMenu: () => void;
}

function DashboardMenuDeleteItem({ dashboard, onDashboardDelete, closeMenu }: DashboardMenuDeleteItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  const dashboardDeleted = (dashboard: Dashboard) => {
    onDashboardDelete(dashboard);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }
  
  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_DashboardMenuItem + ` ${dashboard === null ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Trash2 className={TW_DashboardMenuItemIcon} />{t("dashboard.button.delete.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("dashboard.dialog.delete.title")}</DialogTitle>
          <DialogDescription>{t("dashboard.dialog.delete.description")}</DialogDescription>
        </DialogHeader>
        {dashboard && <DashboardDeleteForm dashboard={dashboard} onDashboardDelete={dashboardDeleted} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface DashboardDeleteFormProps {
  dashboard: Dashboard;
  onDashboardDelete: (dashboard: Dashboard) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function DashboardDeleteForm({ dashboard, onDashboardDelete, onCancel }: DashboardDeleteFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const formSchema = z.object({})
  const form = useForm<z.infer<typeof formSchema>>({})
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  function onSubmit(values: z.infer<typeof formSchema>) {

    if (workspace === null) {
      console.error("No workspace selected");
      return;
    }

    del(`${BASE_URL}/model/workspaces/${workspace._key}/dashboards/${dashboard._key}`, null).then(response => {
      onDashboardDelete(dashboard);
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="ButtonList">
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default DashboardMenuDeleteItem;
